import { loadStripe } from '@stripe/stripe-js';
import { createProject } from './firestoreService';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Project posting fees
export const PROJECT_FEES = {
  BASIC: 0,        // Free project posting
  FEATURED: 25,    // Featured project posting fee
  PREMIUM: 50      // Premium project posting with extra features
};

// Create Stripe checkout session for project posting
export const createProjectCheckoutSession = async (userId, projectData, postingType = 'BASIC') => {
  try {
    const fee = PROJECT_FEES[postingType];
    
    if (fee === 0) {
      // Free posting - create project directly
      return await createProject(projectData, userId);
    }

    // Paid posting - create checkout session
    const functionUrl = 'https://us-central1-the-apn.cloudfunctions.net/createProjectCheckoutSession';

    const requestBody = {
      userId,
      projectData,
      postingType,
      fee,
      successUrl: `${window.location.origin}/projects/payment/success`,
      cancelUrl: `${window.location.origin}/projects/payment/cancel`
    };

    console.log('Creating project checkout session:', requestBody);

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      let errorMessage = 'Failed to create checkout session';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (e) {
        errorMessage = `Payment service error (${response.status})`;
      }
      throw new Error(errorMessage);
    }

    const sessionData = await response.json();
    console.log('Checkout session created:', sessionData);

    return sessionData;
  } catch (error) {
    console.error('Error creating project checkout session:', error);
    throw error;
  }
};

// Create PayPal order for project posting
export const createProjectPayPalOrder = async (userId, projectData, postingType = 'BASIC') => {
  try {
    const fee = PROJECT_FEES[postingType];
    
    if (fee === 0) {
      // Free posting - create project directly
      return await createProject(projectData, userId);
    }

    // Paid posting - create PayPal order
    const functionUrl = 'https://us-central1-the-apn.cloudfunctions.net/createProjectPayPalOrder';

    const requestBody = {
      userId,
      projectData,
      postingType,
      fee,
      successUrl: `${window.location.origin}/projects/payment/success`,
      cancelUrl: `${window.location.origin}/projects/payment/cancel`
    };

    console.log('Creating project PayPal order:', requestBody);

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      let errorMessage = 'Failed to create PayPal order';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (e) {
        errorMessage = `PayPal service error (${response.status})`;
      }
      throw new Error(errorMessage);
    }

    const orderData = await response.json();
    console.log('PayPal order created:', orderData);

    return orderData;
  } catch (error) {
    console.error('Error creating project PayPal order:', error);
    throw error;
  }
};

// Handle successful project payment
export const handleProjectPaymentSuccess = async (sessionId, userId) => {
  try {
    // The project creation will be handled by the webhook
    // This function can be used for additional client-side logic
    console.log('Project payment successful:', { sessionId, userId });
    
    return {
      success: true,
      message: 'Project posted successfully!'
    };
  } catch (error) {
    console.error('Error handling project payment success:', error);
    throw error;
  }
};

// Get project posting fee
export const getProjectPostingFee = (featured = false, premium = false) => {
  if (premium) return PROJECT_FEES.PREMIUM;
  if (featured) return PROJECT_FEES.FEATURED;
  return PROJECT_FEES.BASIC;
};

// Validate project posting type
export const validatePostingType = (postingType) => {
  return Object.keys(PROJECT_FEES).includes(postingType);
};

export default {
  PROJECT_FEES,
  createProjectCheckoutSession,
  createProjectPayPalOrder,
  handleProjectPaymentSuccess,
  getProjectPostingFee,
  validatePostingType
};
